'use client';

import { useState, useMemo, useRef, useEffect } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import CopyButton from '@/components/tools/CopyButton';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type CompanyType =
  | 'bigTech'
  | 'startup'
  | 'corporate'
  | 'consulting'
  | 'government';

type SeniorityLevel =
  | 'Entry'
  | 'Mid'
  | 'Senior'
  | 'Lead'
  | 'Director'
  | 'VP'
  | 'C-Suite';

interface TitleMapping {
  canonical: string;
  category: string;
  seniority: SeniorityLevel;
  equivalents: Record<CompanyType, string>;
  alternativeTitles: string[];
  description: string;
}

/* ------------------------------------------------------------------ */
/*  Company type labels and colors                                     */
/* ------------------------------------------------------------------ */

const COMPANY_TYPE_LABELS: Record<CompanyType, string> = {
  bigTech: 'Big Tech',
  startup: 'Startup',
  corporate: 'Corporate / Enterprise',
  consulting: 'Consulting',
  government: 'Government / Public Sector',
};

const COMPANY_TYPE_COLORS: Record<CompanyType, string> = {
  bigTech: 'bg-purple-100 text-purple-800 border-purple-200',
  startup: 'bg-orange-100 text-orange-800 border-orange-200',
  corporate: 'bg-blue-100 text-blue-800 border-blue-200',
  consulting: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  government: 'bg-slate-100 text-slate-800 border-slate-200',
};

const SENIORITY_COLORS: Record<SeniorityLevel, string> = {
  Entry: 'bg-green-100 text-green-700',
  Mid: 'bg-blue-100 text-blue-700',
  Senior: 'bg-indigo-100 text-indigo-700',
  Lead: 'bg-violet-100 text-violet-700',
  Director: 'bg-amber-100 text-amber-700',
  VP: 'bg-orange-100 text-orange-700',
  'C-Suite': 'bg-red-100 text-red-700',
};

const CATEGORIES = [
  'All',
  'Engineering',
  'Product',
  'Design',
  'Data & Analytics',
  'Marketing',
  'Sales',
  'Human Resources',
  'Finance',
  'Operations',
  'Executive',
];

/* ------------------------------------------------------------------ */
/*  Hardcoded title data (40+ roles)                                   */
/* ------------------------------------------------------------------ */

const TITLE_DATA: TitleMapping[] = [
  // --- Engineering ---
  {
    canonical: 'Software Engineer',
    category: 'Engineering',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'Software Engineer II (L4)',
      startup: 'Full-Stack Developer',
      corporate: 'Application Developer II',
      consulting: 'Technology Consultant',
      government: 'IT Specialist (Applications)',
    },
    alternativeTitles: ['Programmer', 'Software Developer', 'Web Developer', 'Application Engineer'],
    description: 'Designs, builds, and maintains software applications and systems.',
  },
  {
    canonical: 'Senior Software Engineer',
    category: 'Engineering',
    seniority: 'Senior',
    equivalents: {
      bigTech: 'Senior Software Engineer (L5)',
      startup: 'Senior Developer / Tech Lead',
      corporate: 'Senior Application Developer',
      consulting: 'Senior Technology Consultant',
      government: 'Senior IT Specialist',
    },
    alternativeTitles: ['Staff Developer', 'Senior Programmer', 'Senior Full-Stack Engineer', 'Software Engineer III'],
    description: 'Leads technical design and implementation of complex software systems with significant autonomy.',
  },
  {
    canonical: 'Staff Engineer',
    category: 'Engineering',
    seniority: 'Lead',
    equivalents: {
      bigTech: 'Staff Software Engineer (L6)',
      startup: 'Principal Engineer',
      corporate: 'Lead Technical Architect',
      consulting: 'Principal Technology Advisor',
      government: 'Lead IT Specialist',
    },
    alternativeTitles: ['Principal Developer', 'Technical Lead', 'Distinguished Engineer', 'Platform Architect'],
    description: 'Sets technical direction across multiple teams and drives architecture decisions at the org level.',
  },
  {
    canonical: 'Engineering Manager',
    category: 'Engineering',
    seniority: 'Lead',
    equivalents: {
      bigTech: 'Engineering Manager (M1)',
      startup: 'Head of Engineering',
      corporate: 'IT Development Manager',
      consulting: 'Technology Practice Lead',
      government: 'IT Branch Chief',
    },
    alternativeTitles: ['Software Development Manager', 'Dev Manager', 'Technical Manager', 'Team Lead'],
    description: 'Manages a team of engineers, balancing people leadership with technical strategy.',
  },
  {
    canonical: 'Frontend Developer',
    category: 'Engineering',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'Frontend Engineer (L4)',
      startup: 'UI Engineer',
      corporate: 'Web Developer II',
      consulting: 'Digital Experience Developer',
      government: 'Web Application Specialist',
    },
    alternativeTitles: ['UI Developer', 'Client-Side Engineer', 'JavaScript Developer', 'React Developer'],
    description: 'Builds and maintains user-facing web interfaces and experiences.',
  },
  {
    canonical: 'Backend Developer',
    category: 'Engineering',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'Backend Engineer (L4)',
      startup: 'Server-Side Engineer',
      corporate: 'Systems Developer II',
      consulting: 'Integration Developer',
      government: 'Applications Programmer',
    },
    alternativeTitles: ['API Developer', 'Server Engineer', 'Platform Developer', 'Systems Programmer'],
    description: 'Designs and builds server-side logic, APIs, and database integrations.',
  },
  {
    canonical: 'DevOps Engineer',
    category: 'Engineering',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'Site Reliability Engineer (SRE)',
      startup: 'Infrastructure Engineer',
      corporate: 'Systems Engineer II',
      consulting: 'Cloud Infrastructure Consultant',
      government: 'IT Systems Administrator',
    },
    alternativeTitles: ['Platform Engineer', 'Cloud Engineer', 'Release Engineer', 'Build & Release Engineer'],
    description: 'Manages CI/CD pipelines, infrastructure automation, and system reliability.',
  },
  {
    canonical: 'QA Engineer',
    category: 'Engineering',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'Software Engineer in Test (SET)',
      startup: 'QA Automation Engineer',
      corporate: 'Quality Assurance Analyst II',
      consulting: 'Test Consultant',
      government: 'IT Quality Assurance Specialist',
    },
    alternativeTitles: ['Test Engineer', 'SDET', 'Quality Engineer', 'Automation Engineer'],
    description: 'Ensures software quality through automated and manual testing strategies.',
  },
  {
    canonical: 'Mobile Developer',
    category: 'Engineering',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'Mobile Software Engineer (L4)',
      startup: 'iOS/Android Developer',
      corporate: 'Mobile Application Developer II',
      consulting: 'Mobile Solutions Developer',
      government: 'Mobile Applications Specialist',
    },
    alternativeTitles: ['iOS Developer', 'Android Developer', 'Mobile App Engineer', 'React Native Developer'],
    description: 'Builds and maintains native or cross-platform mobile applications.',
  },
  // --- Product ---
  {
    canonical: 'Product Manager',
    category: 'Product',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'Product Manager (L5)',
      startup: 'Product Owner',
      corporate: 'Business Analyst / Product Lead',
      consulting: 'Management Consultant (Product)',
      government: 'Program Analyst',
    },
    alternativeTitles: ['PM', 'Product Lead', 'Technical Product Manager', 'Digital Product Manager'],
    description: 'Defines product vision, strategy, and roadmap while working cross-functionally to deliver value.',
  },
  {
    canonical: 'Senior Product Manager',
    category: 'Product',
    seniority: 'Senior',
    equivalents: {
      bigTech: 'Senior Product Manager (L6)',
      startup: 'Head of Product',
      corporate: 'Senior Business Analyst',
      consulting: 'Senior Engagement Manager',
      government: 'Senior Program Analyst',
    },
    alternativeTitles: ['Lead PM', 'Group Product Manager', 'Principal Product Manager', 'Staff PM'],
    description: 'Leads product strategy for a major product area, mentoring PMs and driving business outcomes.',
  },
  {
    canonical: 'Product Designer',
    category: 'Product',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'Product Designer (L4)',
      startup: 'UI/UX Designer',
      corporate: 'Digital Designer II',
      consulting: 'Experience Design Consultant',
      government: 'Visual Information Specialist',
    },
    alternativeTitles: ['UX Designer', 'Interaction Designer', 'Design Generalist', 'Digital Product Designer'],
    description: 'Designs end-to-end user experiences from research and wireframes to high-fidelity prototypes.',
  },
  {
    canonical: 'Project Manager',
    category: 'Product',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'Technical Program Manager (TPM)',
      startup: 'Delivery Lead',
      corporate: 'Project Manager II',
      consulting: 'Engagement Manager',
      government: 'Project Officer / Program Coordinator',
    },
    alternativeTitles: ['Program Manager', 'Scrum Master', 'Delivery Manager', 'Implementation Manager'],
    description: 'Coordinates cross-functional teams, timelines, and deliverables to execute projects successfully.',
  },
  {
    canonical: 'Scrum Master',
    category: 'Product',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'Agile Program Manager',
      startup: 'Delivery Lead',
      corporate: 'Agile Coach',
      consulting: 'Agile Transformation Consultant',
      government: 'IT Project Lead (Agile)',
    },
    alternativeTitles: ['Agile Lead', 'Iteration Manager', 'Sprint Manager', 'Agile Facilitator'],
    description: 'Facilitates agile processes and removes impediments so teams can deliver effectively.',
  },
  // --- Design ---
  {
    canonical: 'UX Designer',
    category: 'Design',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'UX Designer (L4)',
      startup: 'Product Designer',
      corporate: 'User Experience Analyst II',
      consulting: 'UX Strategy Consultant',
      government: 'Human Factors Specialist',
    },
    alternativeTitles: ['Interaction Designer', 'Experience Designer', 'UI/UX Designer', 'Usability Specialist'],
    description: 'Researches user needs and designs intuitive interfaces that solve real problems.',
  },
  {
    canonical: 'UX Researcher',
    category: 'Design',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'UX Researcher (L4)',
      startup: 'User Researcher',
      corporate: 'Consumer Insights Analyst',
      consulting: 'Research Consultant',
      government: 'Survey Statistician / Research Analyst',
    },
    alternativeTitles: ['Usability Researcher', 'Design Researcher', 'User Insights Analyst', 'Customer Research Specialist'],
    description: 'Conducts qualitative and quantitative research to inform product and design decisions.',
  },
  {
    canonical: 'Visual Designer',
    category: 'Design',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'Visual Designer (L4)',
      startup: 'Brand Designer',
      corporate: 'Graphic Designer II',
      consulting: 'Creative Consultant',
      government: 'Visual Information Specialist',
    },
    alternativeTitles: ['Graphic Designer', 'Brand Designer', 'Digital Designer', 'Creative Designer'],
    description: 'Creates visual identities, UI components, and marketing materials that align with brand standards.',
  },
  {
    canonical: 'Design Director',
    category: 'Design',
    seniority: 'Director',
    equivalents: {
      bigTech: 'Director of Design',
      startup: 'VP of Design / Head of Design',
      corporate: 'Creative Director',
      consulting: 'Design Practice Leader',
      government: 'Chief Design Officer',
    },
    alternativeTitles: ['Head of Design', 'Creative Director', 'Design Lead', 'VP Design'],
    description: 'Sets design vision and strategy across the organization, managing design teams and standards.',
  },
  // --- Data & Analytics ---
  {
    canonical: 'Data Scientist',
    category: 'Data & Analytics',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'Data Scientist (L4)',
      startup: 'ML Engineer / Data Scientist',
      corporate: 'Advanced Analytics Analyst II',
      consulting: 'Analytics Consultant',
      government: 'Mathematical Statistician',
    },
    alternativeTitles: ['ML Engineer', 'Research Scientist', 'Applied Scientist', 'Quantitative Analyst'],
    description: 'Applies statistical methods and machine learning to extract insights and build predictive models.',
  },
  {
    canonical: 'Data Analyst',
    category: 'Data & Analytics',
    seniority: 'Entry',
    equivalents: {
      bigTech: 'Data Analyst / Business Intelligence Analyst',
      startup: 'Analytics Engineer',
      corporate: 'Business Intelligence Analyst II',
      consulting: 'Business Analytics Associate',
      government: 'Statistical Assistant / Program Analyst',
    },
    alternativeTitles: ['BI Analyst', 'Reporting Analyst', 'Insights Analyst', 'Analytics Specialist'],
    description: 'Collects, processes, and analyzes data to support business decision-making and reporting.',
  },
  {
    canonical: 'Data Engineer',
    category: 'Data & Analytics',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'Data Engineer (L4)',
      startup: 'Backend / Data Engineer',
      corporate: 'ETL Developer / Data Integration Engineer',
      consulting: 'Data Platform Consultant',
      government: 'Database Administrator / Data Management Specialist',
    },
    alternativeTitles: ['ETL Developer', 'Big Data Engineer', 'Pipeline Engineer', 'Data Platform Engineer'],
    description: 'Builds and maintains data pipelines, warehouses, and infrastructure to enable analytics at scale.',
  },
  {
    canonical: 'Machine Learning Engineer',
    category: 'Data & Analytics',
    seniority: 'Senior',
    equivalents: {
      bigTech: 'ML Engineer (L5)',
      startup: 'AI Engineer',
      corporate: 'Senior Advanced Analytics Engineer',
      consulting: 'AI/ML Solutions Architect',
      government: 'Computer Scientist (AI/ML)',
    },
    alternativeTitles: ['AI Engineer', 'Deep Learning Engineer', 'NLP Engineer', 'Applied ML Scientist'],
    description: 'Designs and deploys machine learning systems at production scale.',
  },
  // --- Marketing ---
  {
    canonical: 'Marketing Manager',
    category: 'Marketing',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'Product Marketing Manager',
      startup: 'Head of Marketing / Growth Lead',
      corporate: 'Marketing Manager II',
      consulting: 'Marketing Strategy Consultant',
      government: 'Public Affairs Specialist',
    },
    alternativeTitles: ['Brand Manager', 'Growth Manager', 'Digital Marketing Manager', 'Campaign Manager'],
    description: 'Develops and executes marketing strategies to drive awareness, engagement, and revenue.',
  },
  {
    canonical: 'Content Strategist',
    category: 'Marketing',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'Content Strategist',
      startup: 'Content Lead / Head of Content',
      corporate: 'Communications Specialist II',
      consulting: 'Content Strategy Consultant',
      government: 'Public Information Officer',
    },
    alternativeTitles: ['Content Manager', 'Editorial Strategist', 'Content Marketing Manager', 'Copywriter Lead'],
    description: 'Plans, creates, and manages content across channels to support brand and business goals.',
  },
  {
    canonical: 'SEO Specialist',
    category: 'Marketing',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'SEO Analyst / Search Specialist',
      startup: 'Growth Marketer (SEO)',
      corporate: 'Digital Marketing Specialist (SEO)',
      consulting: 'Search Marketing Consultant',
      government: 'Web Content Specialist',
    },
    alternativeTitles: ['Search Engine Optimizer', 'Organic Growth Specialist', 'SEO Manager', 'SEM Specialist'],
    description: 'Optimizes web content and technical infrastructure to improve organic search rankings.',
  },
  {
    canonical: 'Social Media Manager',
    category: 'Marketing',
    seniority: 'Entry',
    equivalents: {
      bigTech: 'Social Media Specialist',
      startup: 'Community Manager',
      corporate: 'Social Media Coordinator',
      consulting: 'Digital Communications Consultant',
      government: 'Digital Outreach Specialist',
    },
    alternativeTitles: ['Community Manager', 'Social Strategist', 'Digital Content Creator', 'Engagement Manager'],
    description: 'Manages brand presence across social platforms, creates content, and engages communities.',
  },
  // --- Sales ---
  {
    canonical: 'Sales Representative',
    category: 'Sales',
    seniority: 'Entry',
    equivalents: {
      bigTech: 'Account Executive',
      startup: 'Business Development Rep (BDR)',
      corporate: 'Sales Associate II',
      consulting: 'Business Development Associate',
      government: 'Contract Specialist',
    },
    alternativeTitles: ['Account Manager', 'Sales Exec', 'Inside Sales Rep', 'Business Development Executive'],
    description: 'Identifies prospects, builds relationships, and closes deals to generate revenue.',
  },
  {
    canonical: 'Sales Manager',
    category: 'Sales',
    seniority: 'Lead',
    equivalents: {
      bigTech: 'Sales Manager / Regional Lead',
      startup: 'Head of Sales',
      corporate: 'District Sales Manager',
      consulting: 'Client Partner',
      government: 'Contracting Officer',
    },
    alternativeTitles: ['Regional Sales Manager', 'Area Manager', 'Sales Team Lead', 'Revenue Manager'],
    description: 'Leads a sales team, sets quotas, coaches reps, and drives regional or segment revenue.',
  },
  {
    canonical: 'Account Executive',
    category: 'Sales',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'Enterprise Account Executive',
      startup: 'Closing Rep / AE',
      corporate: 'Senior Sales Executive',
      consulting: 'Client Engagement Manager',
      government: 'Business Opportunity Specialist',
    },
    alternativeTitles: ['Enterprise AE', 'Strategic AE', 'Key Account Manager', 'Sales Consultant'],
    description: 'Manages full sales cycle for mid-market or enterprise accounts from discovery to close.',
  },
  // --- Human Resources ---
  {
    canonical: 'HR Manager',
    category: 'Human Resources',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'People Operations Manager',
      startup: 'Head of People',
      corporate: 'Human Resources Manager II',
      consulting: 'HR Advisory Manager',
      government: 'Human Resources Specialist (GS-12/13)',
    },
    alternativeTitles: ['People Manager', 'HR Business Partner', 'Employee Relations Manager', 'People Partner'],
    description: 'Oversees HR functions including employee relations, policy, benefits, and compliance.',
  },
  {
    canonical: 'Recruiter',
    category: 'Human Resources',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'Technical Recruiter',
      startup: 'Talent Acquisition Lead',
      corporate: 'Recruiting Specialist II',
      consulting: 'Talent Acquisition Consultant',
      government: 'Human Resources Specialist (Recruitment)',
    },
    alternativeTitles: ['Talent Acquisition Specialist', 'Staffing Specialist', 'Talent Partner', 'Sourcer'],
    description: 'Sources, screens, and hires candidates to fill open positions across the organization.',
  },
  {
    canonical: 'HR Director',
    category: 'Human Resources',
    seniority: 'Director',
    equivalents: {
      bigTech: 'Director of People Operations',
      startup: 'VP of People',
      corporate: 'Director of Human Resources',
      consulting: 'HR Practice Director',
      government: 'Chief Human Capital Officer',
    },
    alternativeTitles: ['VP People', 'Chief People Officer', 'Head of HR', 'People Director'],
    description: 'Sets HR strategy, leads the HR function, and partners with executive leadership on talent initiatives.',
  },
  // --- Finance ---
  {
    canonical: 'Financial Analyst',
    category: 'Finance',
    seniority: 'Entry',
    equivalents: {
      bigTech: 'Financial Analyst (L3)',
      startup: 'Finance Lead / FP&A Analyst',
      corporate: 'Financial Analyst II',
      consulting: 'Financial Advisory Associate',
      government: 'Budget Analyst (GS-9/11)',
    },
    alternativeTitles: ['FP&A Analyst', 'Business Analyst (Finance)', 'Investment Analyst', 'Corporate Finance Analyst'],
    description: 'Builds financial models, forecasts, and analyses to support business planning and investment decisions.',
  },
  {
    canonical: 'Finance Manager',
    category: 'Finance',
    seniority: 'Lead',
    equivalents: {
      bigTech: 'Finance Manager',
      startup: 'Head of Finance',
      corporate: 'Finance Manager II / Controller',
      consulting: 'Financial Advisory Manager',
      government: 'Budget Officer',
    },
    alternativeTitles: ['Controller', 'FP&A Manager', 'Accounting Manager', 'Financial Planning Manager'],
    description: 'Manages financial planning, reporting, and analysis for a business unit or company.',
  },
  {
    canonical: 'Accountant',
    category: 'Finance',
    seniority: 'Entry',
    equivalents: {
      bigTech: 'Staff Accountant',
      startup: 'Bookkeeper / Accountant',
      corporate: 'Accountant II',
      consulting: 'Audit Associate',
      government: 'Accountant (GS-9/11)',
    },
    alternativeTitles: ['Staff Accountant', 'Bookkeeper', 'Accounting Specialist', 'Tax Accountant'],
    description: 'Manages financial records, prepares reports, and ensures compliance with accounting standards.',
  },
  // --- Operations ---
  {
    canonical: 'Operations Manager',
    category: 'Operations',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'Operations Manager / Business Operations',
      startup: 'Head of Ops / COO (small co)',
      corporate: 'Operations Manager II',
      consulting: 'Operations Transformation Consultant',
      government: 'Management Analyst (GS-12/13)',
    },
    alternativeTitles: ['Business Operations Manager', 'BizOps Manager', 'Process Manager', 'Ops Lead'],
    description: 'Optimizes processes, manages cross-functional workflows, and drives operational efficiency.',
  },
  {
    canonical: 'Business Analyst',
    category: 'Operations',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'Business Systems Analyst',
      startup: 'Strategy & Ops Analyst',
      corporate: 'Business Analyst II',
      consulting: 'Associate Consultant',
      government: 'Management Analyst',
    },
    alternativeTitles: ['Systems Analyst', 'Process Analyst', 'Requirements Analyst', 'Solutions Analyst'],
    description: 'Bridges business needs and technology solutions by gathering requirements and driving process improvements.',
  },
  {
    canonical: 'Customer Success Manager',
    category: 'Operations',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'Customer Success Manager',
      startup: 'Account Manager / CS Lead',
      corporate: 'Client Relationship Manager',
      consulting: 'Client Success Advisor',
      government: 'Constituent Services Specialist',
    },
    alternativeTitles: ['Client Success Manager', 'Customer Experience Manager', 'Relationship Manager', 'CSM'],
    description: 'Ensures customers achieve their goals with the product, driving retention and expansion.',
  },
  {
    canonical: 'Supply Chain Manager',
    category: 'Operations',
    seniority: 'Mid',
    equivalents: {
      bigTech: 'Supply Chain Program Manager',
      startup: 'Logistics Lead',
      corporate: 'Supply Chain Manager II',
      consulting: 'Supply Chain Strategy Consultant',
      government: 'Logistics Management Specialist',
    },
    alternativeTitles: ['Logistics Manager', 'Procurement Manager', 'Materials Manager', 'Distribution Manager'],
    description: 'Manages procurement, logistics, and distribution to optimize the end-to-end supply chain.',
  },
  // --- Executive ---
  {
    canonical: 'VP of Engineering',
    category: 'Executive',
    seniority: 'VP',
    equivalents: {
      bigTech: 'Vice President of Engineering',
      startup: 'CTO / VP Engineering',
      corporate: 'Vice President, IT Development',
      consulting: 'Managing Director, Technology',
      government: 'Deputy CIO',
    },
    alternativeTitles: ['SVP Engineering', 'Head of Engineering', 'CTO', 'VP Technology'],
    description: 'Leads the entire engineering organization, setting technical vision and managing engineering leadership.',
  },
  {
    canonical: 'Chief Technology Officer',
    category: 'Executive',
    seniority: 'C-Suite',
    equivalents: {
      bigTech: 'CTO / SVP Engineering',
      startup: 'CTO / Co-Founder (Technical)',
      corporate: 'Chief Technology Officer',
      consulting: 'Chief Technology Advisor',
      government: 'Chief Information Officer',
    },
    alternativeTitles: ['CIO', 'Chief Digital Officer', 'Chief Information Officer', 'VP Technology'],
    description: 'Defines the technology strategy and architecture for the entire organization.',
  },
  {
    canonical: 'Chief Marketing Officer',
    category: 'Executive',
    seniority: 'C-Suite',
    equivalents: {
      bigTech: 'CMO / VP Marketing',
      startup: 'Head of Growth / CMO',
      corporate: 'Chief Marketing Officer',
      consulting: 'Managing Director, Marketing Practice',
      government: 'Director of Communications',
    },
    alternativeTitles: ['VP Marketing', 'Head of Marketing', 'Chief Growth Officer', 'Chief Brand Officer'],
    description: 'Leads all marketing functions, brand strategy, and go-to-market efforts for the organization.',
  },
  {
    canonical: 'Chief Financial Officer',
    category: 'Executive',
    seniority: 'C-Suite',
    equivalents: {
      bigTech: 'CFO',
      startup: 'CFO / Head of Finance',
      corporate: 'Chief Financial Officer',
      consulting: 'Managing Director, Finance',
      government: 'Chief Financial Officer / Comptroller',
    },
    alternativeTitles: ['VP Finance', 'Head of Finance', 'Treasurer', 'Chief Accounting Officer'],
    description: 'Oversees financial strategy, reporting, fundraising, and fiscal health of the organization.',
  },
  {
    canonical: 'Chief Operating Officer',
    category: 'Executive',
    seniority: 'C-Suite',
    equivalents: {
      bigTech: 'COO / VP Operations',
      startup: 'COO / Co-Founder (Operations)',
      corporate: 'Chief Operating Officer',
      consulting: 'Chief Operating Officer',
      government: 'Deputy Administrator / Chief of Staff',
    },
    alternativeTitles: ['VP Operations', 'Head of Operations', 'General Manager', 'Chief of Staff'],
    description: 'Manages day-to-day business operations and translates strategy into execution across the company.',
  },
];

/* ------------------------------------------------------------------ */
/*  Resume tips data                                                   */
/* ------------------------------------------------------------------ */

const RESUME_TIPS = [
  {
    title: 'Match the target company\'s language',
    body: 'If you are applying to a big tech company, use their title conventions (e.g., "Software Engineer II" instead of "Application Developer"). Scan their job postings for clues.',
  },
  {
    title: 'Use the most recognizable title',
    body: 'If your actual title is obscure (e.g., "Digital Solutions Ninja"), translate it to the industry-standard equivalent. Recruiters search for common titles.',
  },
  {
    title: 'Don\'t inflate your seniority',
    body: 'Translating titles is about clarity, not embellishment. If you were a coordinator, don\'t list yourself as a manager. Interviewers will uncover discrepancies.',
  },
  {
    title: 'Add context in parentheses when helpful',
    body: 'If your title was unique, clarify it: "Technology Consultant (equivalent to Senior Software Engineer)". This helps ATS systems and human readers.',
  },
  {
    title: 'Consider the ATS implications',
    body: 'Applicant tracking systems scan for exact keyword matches. Using the same title as the job posting increases your chances of getting past the first filter.',
  },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function JobTitleContent() {
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedResult, setSelectedResult] = useState<TitleMapping | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Close suggestions on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtered data based on search + category
  const filteredTitles = useMemo(() => {
    const q = query.toLowerCase().trim();
    return TITLE_DATA.filter((t) => {
      const matchesCategory =
        selectedCategory === 'All' || t.category === selectedCategory;
      if (!q) return matchesCategory;
      const matchesSearch =
        t.canonical.toLowerCase().includes(q) ||
        t.alternativeTitles.some((alt) => alt.toLowerCase().includes(q)) ||
        Object.values(t.equivalents).some((eq) =>
          eq.toLowerCase().includes(q)
        ) ||
        t.category.toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    });
  }, [query, selectedCategory]);

  // Autocomplete suggestions
  const suggestions = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q || q.length < 2) return [];
    const matches: { label: string; mapping: TitleMapping }[] = [];
    for (const t of TITLE_DATA) {
      if (t.canonical.toLowerCase().includes(q)) {
        matches.push({ label: t.canonical, mapping: t });
      }
      for (const alt of t.alternativeTitles) {
        if (alt.toLowerCase().includes(q)) {
          matches.push({ label: `${alt} → ${t.canonical}`, mapping: t });
        }
      }
      for (const eq of Object.values(t.equivalents)) {
        if (eq.toLowerCase().includes(q)) {
          matches.push({ label: `${eq} → ${t.canonical}`, mapping: t });
        }
      }
    }
    // Deduplicate by canonical
    const seen = new Set<string>();
    return matches.filter((m) => {
      if (seen.has(m.mapping.canonical)) return false;
      seen.add(m.mapping.canonical);
      return true;
    }).slice(0, 8);
  }, [query]);

  function handleSelectSuggestion(mapping: TitleMapping) {
    setQuery(mapping.canonical);
    setSelectedResult(mapping);
    setShowSuggestions(false);
  }

  function handleTranslate() {
    setShowSuggestions(false);
    if (filteredTitles.length === 1) {
      setSelectedResult(filteredTitles[0]);
    } else if (filteredTitles.length > 0) {
      // Exact match first
      const exact = filteredTitles.find(
        (t) => t.canonical.toLowerCase() === query.toLowerCase().trim()
      );
      if (exact) {
        setSelectedResult(exact);
      } else {
        setSelectedResult(null); // Show the list instead
      }
    } else {
      setSelectedResult(null);
    }
  }

  function handleSelectFromList(mapping: TitleMapping) {
    setSelectedResult(mapping);
    setQuery(mapping.canonical);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function buildCopyText(mapping: TitleMapping): string {
    const lines: string[] = [`Job Title: ${mapping.canonical}`];
    lines.push(`Seniority: ${mapping.seniority}`);
    lines.push(`Category: ${mapping.category}`);
    lines.push('');
    lines.push('Equivalent Titles:');
    for (const [key, label] of Object.entries(COMPANY_TYPE_LABELS)) {
      lines.push(`  ${label}: ${mapping.equivalents[key as CompanyType]}`);
    }
    lines.push('');
    lines.push(`Alternative Titles: ${mapping.alternativeTitles.join(', ')}`);
    return lines.join('\n');
  }

  const COMPANY_TYPES: CompanyType[] = [
    'bigTech',
    'startup',
    'corporate',
    'consulting',
    'government',
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="Job Title Translator"
        description="See how the same role is named differently across Big Tech, startups, corporations, consulting firms, and government. Find the right title for your resume."
        category="Career Tools"
      />

      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Search / Input Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Translate a Job Title
          </h2>
          <p className="text-gray-600 mb-6">
            Enter any job title to see its equivalents across different company types, or browse the full list below.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setShowSuggestions(true);
                  setSelectedResult(null);
                }}
                onFocus={() => setShowSuggestions(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTranslate();
                }}
                placeholder="e.g., Software Engineer, Product Manager, Data Scientist..."
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition text-base"
              />

              {/* Autocomplete dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div
                  ref={suggestionsRef}
                  className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-20 max-h-72 overflow-y-auto"
                >
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => handleSelectSuggestion(s.mapping)}
                      className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-b-0 text-sm cursor-pointer"
                    >
                      <span className="font-medium text-gray-900">
                        {s.label}
                      </span>
                      <span className="ml-2 text-xs text-gray-400">
                        {s.mapping.category}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={handleTranslate}
              className="bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold py-3 px-8 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-600 transition-all text-base cursor-pointer whitespace-nowrap"
            >
              Translate Title
            </button>
          </div>

          {/* Quick select popular titles */}
          <div className="mt-6">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Popular Titles
            </p>
            <div className="flex flex-wrap gap-2">
              {[
                'Software Engineer',
                'Product Manager',
                'Data Scientist',
                'UX Designer',
                'Marketing Manager',
                'Sales Representative',
                'HR Manager',
                'Financial Analyst',
                'Operations Manager',
                'Project Manager',
              ].map((title) => (
                <button
                  key={title}
                  onClick={() => {
                    setQuery(title);
                    const match = TITLE_DATA.find(
                      (t) => t.canonical === title
                    );
                    if (match) setSelectedResult(match);
                    setShowSuggestions(false);
                  }}
                  className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-blue-100 hover:text-blue-700 transition-colors cursor-pointer font-medium"
                >
                  {title}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Selected Result Detail */}
        {selectedResult && (
          <div className="mt-10 space-y-8">
            {/* Title header */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-2xl font-extrabold text-gray-900 mb-2">
                    {selectedResult.canonical}
                  </h2>
                  <p className="text-gray-600 leading-relaxed">
                    {selectedResult.description}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                      SENIORITY_COLORS[selectedResult.seniority]
                    }`}
                  >
                    {selectedResult.seniority} Level
                  </span>
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-600">
                    {selectedResult.category}
                  </span>
                </div>
              </div>

              {/* Equivalents table */}
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Equivalent Titles Across Company Types
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-3 pr-4 font-semibold text-gray-600 w-48">
                        Company Type
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">
                        Title Used
                      </th>
                      <th className="text-right py-3 pl-4 w-20" />
                    </tr>
                  </thead>
                  <tbody>
                    {COMPANY_TYPES.map((type) => (
                      <tr
                        key={type}
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                      >
                        <td className="py-3 pr-4">
                          <span
                            className={`inline-block px-2.5 py-1 rounded-lg text-xs font-bold border ${COMPANY_TYPE_COLORS[type]}`}
                          >
                            {COMPANY_TYPE_LABELS[type]}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-medium text-gray-900">
                          {selectedResult.equivalents[type]}
                        </td>
                        <td className="py-3 pl-4 text-right">
                          <CopyButton
                            text={selectedResult.equivalents[type]}
                            label="Copy"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Copy all button */}
              <div className="mt-4 flex justify-end">
                <CopyButton
                  text={buildCopyText(selectedResult)}
                  label="Copy All"
                  className="bg-blue-50 text-blue-700 hover:bg-blue-100"
                />
              </div>
            </div>

            {/* Alternative Titles */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Other Common Alternative Titles
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                These titles are also used interchangeably for this role in various contexts.
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedResult.alternativeTitles.map((alt) => (
                  <span
                    key={alt}
                    className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm rounded-lg font-medium border border-blue-100"
                  >
                    {alt}
                  </span>
                ))}
              </div>
            </div>

            {/* Seniority level mapping */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Seniority Level Context
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                {(
                  Object.keys(SENIORITY_COLORS) as SeniorityLevel[]
                ).map((level) => (
                  <div
                    key={level}
                    className={`text-center p-3 rounded-xl border-2 transition-all ${
                      level === selectedResult.seniority
                        ? 'border-blue-500 shadow-md scale-105 bg-blue-50'
                        : 'border-gray-100 bg-gray-50 opacity-60'
                    }`}
                  >
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold mb-1 ${SENIORITY_COLORS[level]}`}
                    >
                      {level}
                    </span>
                    {level === selectedResult.seniority && (
                      <p className="text-xs text-blue-600 font-semibold mt-1">
                        This role
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Category Filter + Full Browse List */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Browse All Title Translations
          </h2>

          {/* Category pills */}
          <div className="flex flex-wrap gap-2 mb-8">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setSelectedCategory(cat);
                  setSelectedResult(null);
                }}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer ${
                  selectedCategory === cat
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-300 hover:text-blue-600'
                }`}
              >
                {cat}
                {cat !== 'All' && (
                  <span className="ml-1.5 text-xs opacity-75">
                    (
                    {
                      TITLE_DATA.filter((t) => t.category === cat)
                        .length
                    }
                    )
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Results count */}
          <p className="text-sm text-gray-500 mb-4">
            Showing {filteredTitles.length} of {TITLE_DATA.length} titles
          </p>

          {/* Title cards grid */}
          {filteredTitles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredTitles.map((mapping) => (
                <button
                  key={mapping.canonical}
                  onClick={() => handleSelectFromList(mapping)}
                  className={`text-left bg-white rounded-2xl shadow border p-6 hover:shadow-lg hover:border-blue-300 transition-all cursor-pointer ${
                    selectedResult?.canonical === mapping.canonical
                      ? 'border-blue-500 ring-2 ring-blue-200'
                      : 'border-gray-100'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h3 className="text-base font-bold text-gray-900">
                      {mapping.canonical}
                    </h3>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${
                          SENIORITY_COLORS[mapping.seniority]
                        }`}
                      >
                        {mapping.seniority}
                      </span>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 mb-3 line-clamp-2">
                    {mapping.description}
                  </p>

                  {/* Mini equivalents preview */}
                  <div className="space-y-1.5">
                    {COMPANY_TYPES.slice(0, 3).map((type) => (
                      <div key={type} className="flex items-center gap-2 text-xs">
                        <span className="text-gray-400 w-20 flex-shrink-0 truncate">
                          {COMPANY_TYPE_LABELS[type]}:
                        </span>
                        <span className="text-gray-700 font-medium truncate">
                          {mapping.equivalents[type]}
                        </span>
                      </div>
                    ))}
                    <p className="text-xs text-blue-600 font-semibold mt-1">
                      + {COMPANY_TYPES.length - 3} more...
                    </p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
              <p className="text-gray-500 text-lg mb-2">No matching titles found</p>
              <p className="text-gray-400 text-sm">
                Try a different search term or browse by category above.
              </p>
            </div>
          )}
        </div>

        {/* Resume Tips Section */}
        <div className="mt-12">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <svg
                className="w-5 h-5 text-amber-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
              Tips for Choosing the Right Title on Your Resume
            </h2>
            <div className="space-y-5">
              {RESUME_TIPS.map((tip, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-200 text-amber-800 text-xs font-bold">
                    {i + 1}
                  </span>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">
                      {tip.title}
                    </p>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {tip.body}
                    </p>
                  </div>
                </div>
              ))}
              <p className="mt-6 pt-4 border-t border-amber-200 text-sm text-gray-500">
                <strong>Sources &amp; Further Reading:</strong>{' '}
                <a href="https://www.onetonline.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">O*NET Online — Standardized Occupation Classifications</a>
                {' · '}
                <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Bureau of Labor Statistics Occupational Outlook Handbook</a>
                {' · '}
                <a href="https://www.careeronestop.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">CareerOneStop — U.S. Department of Labor</a>
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <ToolCTA
          headline="Use the right titles. Land more interviews."
          description="Build a professional, ATS-optimized resume with the right job titles and keywords. Superpower Resume makes it easy."
          buttonText="Build My Resume Free"
        />
      </div>
    </div>
  );
}
